import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import type { IPricingParameterDefinition } from '../../services/pricingParameterService';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';
import {
  formatRecordValue,
  parameterLabel,
  type ParameterRecordRow,
} from './pricingParameterHelpers';
import { getOverrideStatus } from './tenantPricingOverrideHelpers';

export interface PricingParameterRecordsProps {
  rows: ParameterRecordRow[];
  definitions: IPricingParameterDefinition[];
  loading: boolean;
  today: string;
  emptyTitle: string;
  emptySubtitle: string;
  commandBarItems?: ICommandBarItem[];
  selectedId?: number | null;
  onSelectionChange?: (row: ParameterRecordRow) => void;
  onRowDoubleClicked?: (row: ParameterRecordRow) => void;
}

const PricingParameterRecords = ({
  rows,
  definitions,
  loading,
  today,
  emptyTitle,
  emptySubtitle,
  commandBarItems,
  selectedId,
  onSelectionChange,
  onRowDoubleClicked,
}: PricingParameterRecordsProps) => {
  const hasMixedSources = new Set(rows.map((row) => row.source)).size > 1;
  const columns: DataGridColumn<ParameterRecordRow>[] = [
    {
      key: 'key',
      name: 'Parameter',
      minWidth: 140,
      width: 180,
      value: (row) => parameterLabel(row.record.key),
    },
    {
      key: 'value',
      name: 'Value',
      minWidth: 100,
      width: 130,
      value: (row) => formatRecordValue(definitions, row.record),
    },
    {
      key: 'source',
      name: 'Source',
      minWidth: 70,
      width: 90,
      value: (row) => row.source,
    },
    {
      key: 'effectiveFrom',
      name: 'Effective from',
      minWidth: 110,
      width: 140,
      value: (row) => formatDateOnlyForDisplay(row.record.effectiveFrom),
    },
    {
      key: 'effectiveTo',
      name: 'Effective to',
      minWidth: 110,
      width: 140,
      value: (row) =>
        formatDateOnlyForDisplay(row.record.effectiveTo) || 'No end date',
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 80,
      width: 100,
      value: (row) =>
        getOverrideStatus(
          row.record.effectiveFrom,
          row.record.effectiveTo,
          today
        ),
    },
  ];

  return (
    <PagedDataGrid<ParameterRecordRow>
      columns={columns.filter(
        (column) => hasMixedSources || column.key !== 'source'
      )}
      items={rows}
      loading={loading}
      commandBarItems={commandBarItems}
      selectionMode={onSelectionChange ? 'single' : undefined}
      selectedId={onSelectionChange ? selectedId ?? null : undefined}
      onSelectionChange={onSelectionChange}
      onRowDoubleClicked={onRowDoubleClicked}
      fillContainer
      empty={{ title: emptyTitle, subtitle: emptySubtitle }}
    />
  );
};

export default PricingParameterRecords;
